import { useContext, useEffect, useMemo, useState } from 'react';
import { Button, FormProvider, Header, Icon, Input, NavItem, NavList, ThemeContext } from 'mouse-user-interface';
import { docs, categoryOrder } from '../../docs';
import styles from './Nav.module.scss';
import { useLocation, useNavigate } from 'react-router-dom';

function Nav() {
    const [brandColor, setBrandColor] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const { theme, setTheme } = useContext(ThemeContext);
    const nextTheme = theme === 'light' ? 'dark' : 'light';
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        const saved = localStorage.getItem('brandColor');
        if (saved) {
            setBrandColor(saved);
            document.documentElement.style.setProperty('--color-brand', saved);
            return;
        }
        requestAnimationFrame(() => {
            const computed = getComputedStyle(document.documentElement);
            const currentBrand = computed.getPropertyValue('--color-brand').trim();
            if (currentBrand) {
                setBrandColor(currentBrand);
            }
        });
    }, []);

    const groupedResults = useMemo(() => {
        if (!searchQuery) return {};

        const query = searchQuery.toLowerCase();

        const filtered = docs.filter((doc) => {
            return (
                doc.title.toLowerCase().includes(query) ||
                doc.category.toLowerCase().includes(query) ||
                doc.desc.toLowerCase().includes(query) ||
                (doc.interfaces || []).some((i) => i.toLowerCase().includes(query)) ||
                (doc.types || []).some((t) => t.toLowerCase().includes(query))
            );
        });

        const groups = filtered.reduce((acc, doc) => {
            if (!acc[doc.category]) acc[doc.category] = [];
            acc[doc.category].push(doc);
            return acc;
        }, {});
        return groups;
    }, [searchQuery]);

    const closeMobileNav = () => {
        const navToggle = document.getElementById('header-toggle');
        if (navToggle) navToggle.click();
    };

    const createNavHandler = (action) => {
        return async (...args) => {
            closeMobileNav();
            if (typeof action === 'function') {
                await action(...args);
            }
        };
    };

    const toSlug = (str) => str.toLowerCase().replace(/\s+/g, '-').replace(/[^\w-]+/g, '');

    const handleSearchResultClicked = (id) => {
        const slug = toSlug(id);
        const navToggle = document.getElementById('header-toggle');
        navigate(`/docs/${slug}`);
        setSearchQuery('');
        if (navToggle) navToggle.click();
    };

    const toggleTheme = () => {
        setTheme(nextTheme);
    };

    const handleBrandColorChange = (e) => {
        const newColor = e.target.value;
        setBrandColor(newColor);
        localStorage.setItem('brandColor', newColor);
        document.documentElement.style.setProperty('--color-brand', newColor);
    };

    const sortedCategories = useMemo(() => {
        return categoryOrder.filter(cat => groupedResults[cat]);
    }, [groupedResults]);

    return (
        <Header
            className={styles.nav}
            id='main-header'
            sticky
            heading={
                <a href='/' style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <Icon
                        name='Mouse'
                        size={30}
                        className={styles.nav__icon}
                    />
                    <h3 className='desktop-only'>MouseUI</h3>
                </a>
            }
        >
            <NavList align='right' className={styles['nav__nav-list']}>
                <div className={styles.nav__search}>
                    <FormProvider className={styles['nav__search--form']}>
                        <Input
                            style={{ width: '100%' }}
                            placeholder='&#x1F50E;&#xFE0E;  Search'
                            name='search'
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            autoComplete="off"
                        />
                        {searchQuery && (
                            <Button
                                variant='stripped'
                                type='text'
                                className={styles['nav__search--clear']}
                                onClick={() => setSearchQuery('')}
                            >x</Button>
                        )}
                    </FormProvider>
                    {searchQuery && (
                        <div className={styles['nav__search--results']}>
                            {sortedCategories.length === 0 ? (
                                <p>No results found.</p>
                            ) : (
                                sortedCategories.map(category => (
                                    <div key={category} className={styles['nav__search--results--category']}>
                                        <h3 className={styles['nav__search--results--category--title']}>{category.charAt(0).toUpperCase() + category.slice(1)}</h3>
                                        <ul>
                                            {groupedResults[category].map(result => (
                                                <li
                                                    onClick={() => handleSearchResultClicked(result.id)}
                                                    key={result.id}
                                                >
                                                    <div className={styles['nav__search--results--item']}>
                                                        <h3>{result.title}</h3>
                                                        <p>{result.desc}</p>
                                                        <p>
                                                            {[...(result.types || []), ...(result.interfaces || [])].join(' ')}
                                                        </p>
                                                    </div>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                ))
                            )}
                        </div>
                    )}
                </div>
                {/* <NavItem
                    onClick={createNavHandler(() => navigate('/preview'))}
                    className={`${styles.nav__link} ${location.pathname === '/preview' ? styles['nav__link--selected'] : ''}`}
                >
                    Preview
                </NavItem> */}
                <NavItem
                    onClick={createNavHandler(() => navigate('/docs'))}
                    className={`${styles.nav__link} ${location.pathname.startsWith('/docs') ? styles['nav__link--selected'] : ''}`}
                >
                    Documentation
                </NavItem>
                <Button
                    onClick={toggleTheme}
                    variant='stripped'
                    className={styles.nav__item}
                >
                    <p className='handheld-only bold'>Selected Theme</p>
                    <Icon
                        name={theme === 'light' ? 'DarkMode' : 'LightMode'}
                        size={theme === 'light' ? 32 : 40}
                    />
                </Button>
                <Button
                    variant='stripped'
                    className={`${styles.nav__item} desktop-only`}
                >
                    <input
                        type='color'
                        value={brandColor}
                        onChange={handleBrandColorChange}
                        className={styles['nav__color-input']}
                    />
                </Button>
            </NavList>
        </Header>
    );
}

export default Nav;